import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { getFormValues, getFormSyncErrors } from 'redux-form'
import { Dispatch } from 'redux'
import { message } from 'antd'

import { DefaultRootState } from '~/types'
import LocalStorageWrapper from '~/lib/LocalStorageWrapper'
import orderDetailsQuery from '../Queries/orderDetailsQuery'
import { salesOrder_salesOrder_salesOrderLines_items as SalesOrderLineItemType } from '../Queries/__generated__/salesOrder'

const storage = new LocalStorageWrapper()

export const getLocalStorageItem = (key: string) => storage.getItem(key)

export const setLocalStorageItem = (key: string, value: string | boolean) =>
    storage.setItem(key, value)

export const STORAGE_ITEM_CHECKED_5106 = '__HTS_FORM_5106_CHECKED__'

export const TYPE_OF_HTS = {
    PROTOTYPING: 'prototype',
    COMMERCIAL: 'production',
}

export const TITLE_OF_HTS_TYPE = {
    prototype: 'prototyping',
    production: 'commercial',
    unspecified: 'unspecified',
}

export const ORDER_TYPE = {
    PLATFORM: 'platform',
    ERP: 'erp',
}

const FORM = 'htsTableCode'

export const FORM_CONFIG = {
    form: FORM,
}

export const selectFormValues = getFormValues(FORM)
export const formSyncErrors = getFormSyncErrors(FORM)

export const PROTOTYPING_HTS_INITIAL = '9817.85'

export const CBP_FORM_LINK =
    'https://www.cbp.gov/document/forms/form-5106-importer-id-input-record'

const timeToholdMessage = 6

export const htsSubmitModalWidth = 437

export const successMessageHtsSubmission = () => {
    message.success(
        'Customs information submitted successfully!',
        timeToholdMessage,
    )
}

export const successMessageHtsSaveAndFinishLater = () => {
    message.success(
        'Customs information saved successfully!',
        timeToholdMessage,
    )
}

export const errorMessageHtsSubmission = () => {
    message.error(
        'Failed to submit customs information.  If the issue persists, please contact us instead.',
        timeToholdMessage,
    )
}

export const submissionFailedError = () => {
    message.warning(
        'Cannot submit customs information because the order is being imported to our database. Please wait a moment and try again.',
        timeToholdMessage,
    )
}

export const downloadCsv = (type: string) => {
    let csvHeaders

    if (type === TYPE_OF_HTS.PROTOTYPING) {
        csvHeaders = [
            [
                'Part name as listed on Fictiv platform',
                'Item Name',
                'Primary HTS code',
                'Secondary HTS code',
                'Item Description',
                'End Use',
            ],
            [
                'e.g. Sample123',
                'e.g. Aluminum plate',
                'e.g. 9817.85.05',
                'e.g. 8310.00.0000',
                'e.g. Aluminum plate to be installed in a digital camera housing',
                'e.g. Parts to be used in an autonomous automobile',
            ],
        ]
    } else {
        csvHeaders = [
            [
                'Part name as listed on Fictiv platform',
                'Item Name',
                'HTS Code',
                'Item Description',
                'End Use',
            ],
            [
                'e.g. Sample123',
                'e.g. Aluminum plate',
                'e.g. 8310.00.0000',
                'e.g. Aluminum plate to be installed in a digital camera housing',
                'e.g. Parts to be used in an autonomous automobile',
            ],
        ]
    }

    const csvContent = `data:text/csv;charset=utf-8,
        ${csvHeaders.map(e => e.join(',')).join('\n')}`

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')

    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'ClearanceInfo.csv')

    if (document.body) {
        const { body } = document

        body.appendChild(link)
        link.click()
        body.removeChild(link)
    }
}

export type HtsInfoValueType = {
    value: string
    salesOrderLineId?: string
    htsSubmitted: boolean
    usageType: string
}

export type HtsInfoType = {
    [key: string]: HtsInfoValueType
}

export const updateHtsInfo = (
    htsInfo: HtsInfoValueType[],
    salesOrderLines: SalesOrderLineItemType[],
) => {
    const sampleLines = salesOrderLines.filter(
        item => item.processName === 'Injection Molding Samples',
    )

    htsInfo.forEach(htsLineItem => {
        const salesOrderLineId = htsLineItem.salesOrderLineId
        const orderLine = salesOrderLines.filter(
            line => line.id === salesOrderLineId,
        )

        const sampleLine = sampleLines.filter(
            item => item.revision?.id == orderLine[0].revision?.id,
        )

        if (sampleLine.length) {
            const sampleHtsLineItem = {
                ...htsLineItem,
                salesOrderLineId: sampleLine[0].id,
            }

            htsInfo.push(sampleHtsLineItem)
        }
    })
}

export function getHtsInfo({
    formData,
    isItReadyToBeSubmitted,
    typeOfHts,
    salesOrderLines,
}: {
    formData: Record<string, string>
    isItReadyToBeSubmitted: boolean
    typeOfHts: string
    salesOrderLines: SalesOrderLineItemType[]
}) {
    const htsInfo: HtsInfoType = {}

    Object.keys(formData).map(key => {
        const [part, value] = key.split(':')
        const salesOrderLine = salesOrderLines.find(
            l => l?.revision?.id === part,
        )

        htsInfo[part] = {
            ...htsInfo[part],
            [value]: formData[key],
            salesOrderLineId: salesOrderLine?.id,
            htsSubmitted: isItReadyToBeSubmitted,
            usageType: typeOfHts,
        }
    })
    return Object.values(htsInfo)
}

export function getHtsFormValues(saleOrderLines: SalesOrderLineItemType[]) {
    const formData: { [key: string]: string | null } = {}

    saleOrderLines.forEach(line => {
        const { revision, htsDescription, htsApplication, htsCode } = line

        const revisionId = revision?.id

        if (revisionId && htsDescription && htsApplication) {
            formData[`${revisionId}:htsCode`] = htsCode
            formData[`${revisionId}:description`] = htsDescription
            formData[`${revisionId}:application`] = htsApplication
        }
    })
    return formData
}

const updateHts = gql`
    mutation updateHts(
        $orderId: String!
        $htsInput: [HtsUpdateInput!]!
        $isDdpQualified: Boolean!
        $userId: Int
    ) {
        updateHts(
            orderId: $orderId
            orderLinesInput: $htsInput
            isDdpQualified: $isDdpQualified
            userId: $userId
        )
    }
`

type ApolloClientType = ApolloClient<NormalizedCacheObject>

export const updateHtsMutation =
    (
        orderId: string,
        htsInput:
            | {
                  value: string
                  salesOrderLineId?: string
                  htsSubmitted: boolean
                  usageType: string
              }[]
            | null,
        orderName: string,
        isSubmit: boolean,
        isDdpQualified: boolean,
        userId?: number | undefined | null,
        updateHtsSubmitting?: Function,
        // eslint-disable-next-line max-params
    ) =>
    (
        dispatch: Dispatch,
        state: DefaultRootState,
        { client }: { client: ApolloClientType },
    ) => {
        client
            .mutate({
                mutation: updateHts,
                variables: {
                    orderId,
                    htsInput,
                    isDdpQualified,
                    userId,
                },
                refetchQueries: [
                    {
                        query: orderDetailsQuery,
                        variables: {
                            name: orderName,
                        },
                    },
                ],
            })
            .then(() => {
                return !isSubmit && successMessageHtsSaveAndFinishLater()
            })
            .catch(() => {
                errorMessageHtsSubmission()
                return isSubmit && updateHtsSubmitting && updateHtsSubmitting()
            })
    }

export const isFieldRequired = (value: string | undefined) =>
    value ? undefined : 'Required'

export const normalizeHtsInput = (value: string, previousValue: string) => {
    const LIMIT_OF_FIRST_INPUT = 4
    const LIMIT_OF_SECOND_INPUT = 6
    const TOTAL_LENGTH = 10

    if (!value) {
        return value
    }
    const onlyNums = value.replace(/[^\d]/g, '')

    if (!previousValue || value.length > previousValue.length) {
        if (onlyNums.length === LIMIT_OF_FIRST_INPUT) {
            return `${onlyNums}.`
        }
        if (onlyNums.length === LIMIT_OF_SECOND_INPUT) {
            return `${onlyNums.slice(0, LIMIT_OF_FIRST_INPUT)}.${onlyNums.slice(
                LIMIT_OF_FIRST_INPUT,
            )}.`
        }
    }
    if (onlyNums.length <= LIMIT_OF_FIRST_INPUT) {
        return onlyNums
    }
    if (onlyNums.length <= LIMIT_OF_SECOND_INPUT) {
        return `${onlyNums.slice(0, LIMIT_OF_FIRST_INPUT)}.${onlyNums.slice(
            LIMIT_OF_FIRST_INPUT,
        )}`
    }
    return `${onlyNums.slice(0, LIMIT_OF_FIRST_INPUT)}.${onlyNums.slice(
        LIMIT_OF_FIRST_INPUT,
        LIMIT_OF_SECOND_INPUT,
    )}.${onlyNums.slice(LIMIT_OF_SECOND_INPUT, TOTAL_LENGTH)}`
}

export const isSalesOrderLineIdAvailable = ({
    selectedValues,
    typeOfHts,
    salesOrderLines,
}: {
    selectedValues: Record<string, string>
    typeOfHts: string
    salesOrderLines: SalesOrderLineItemType[]
}) => {
    const htsInformation = getHtsInfo({
        formData: selectedValues,
        isItReadyToBeSubmitted: false,
        typeOfHts,
        salesOrderLines,
    })

    return (
        htsInformation &&
        htsInformation.every(htsInfo => htsInfo.salesOrderLineId)
    )
}

export const checkAllErrorsResolved = (errors: { [key: string]: string }) => {
    return Object.values(errors).some(
        val =>
            val === 'Incomplete entry' ||
            val === 'This code is for prototyping parts only' ||
            val === 'Cannot be the same as primary HTS',
    )
}
